import { PublishJobType } from '../enums/publish-job-type';
import * as _ from 'lodash';
import { PublishStepper } from '../models/publish-stepper.model';
import { PublishTaskType } from '../enums/publish-task-type';
import { CdtStatus } from '../../app/cdt-status';
import { PublishStepperLabels } from '../constants/publish-stepper-labels.const';

export class StatusResolver {

    private onboardingAnalyticsSteppers = [
        new PublishStepper({
            taskType: PublishTaskType.ONBOARDING_ANALYTICS_SETUP,
            jobType: PublishJobType.SKYSPARK_CREATE_PROJECT,
            label: PublishStepperLabels.CREATE_SKYSPARK_PROJECT
        }),
        new PublishStepper({
            taskType: PublishTaskType.ONBOARDING_ANALYTICS_SETUP,
            jobType: PublishJobType.SKYSPARK_ENABLE_EXTENSIONS,
            label: PublishStepperLabels.ENABLE_EXTENSIONS
        }),
        new PublishStepper({
            taskType: PublishTaskType.ONBOARDING_ANALYTICS_SETUP,
            jobType: PublishJobType.SKYSPARK_ENABLE_RULES,
            label: PublishStepperLabels.AUTO_CONFIGURE_RULES
        }),
        new PublishStepper({
            taskType: PublishTaskType.ONBOARDING_ANALYTICS_SETUP,
            jobType: PublishJobType.AZURE_ELASTIC_POOL_AND_DB_CREATION,
            label: PublishStepperLabels.CREATE_DATABASE
        }),
        new PublishStepper({
            taskType: PublishTaskType.ONBOARDING_ANALYTICS_SETUP,
            jobType: PublishJobType.AZURE_DB_TABLES_CREATION,
            label: PublishStepperLabels.CREATE_TABLES
        }),
        new PublishStepper({
            taskType: PublishTaskType.ONBOARDING_ANALYTICS_SETUP,
            jobType: PublishJobType.DASHBOARD_CONFIGURATION,
            label: PublishStepperLabels.ENABLE_DASHBOARDS
        }),
        new PublishStepper({
            taskType: PublishTaskType.ONBOARDING_ANALYTICS_SETUP,
            jobType: PublishJobType.SKYSPARK_IMPORT_MODEL,
            label: PublishStepperLabels.EXPORT_MODEL_TO_SKYSPARK
        })
    ];

    private onboardingImportModelSteppers = [
        new PublishStepper({
            taskType: PublishTaskType.ONBOARDING_IMPORT_MODEL,
            jobType: PublishJobType.SKYSPARK_RETRIEVAL,
            label: PublishStepperLabels.SKYSPARK_RETRIEVAL
        }),
        new PublishStepper({
            taskType: PublishTaskType.ONBOARDING_IMPORT_MODEL,
            jobType: PublishJobType.SKYSPARK_ENABLE_EXTENSIONS,
            label: PublishStepperLabels.ENABLE_EXTENSIONS
        }),
        new PublishStepper({
            taskType: PublishTaskType.ONBOARDING_IMPORT_MODEL,
            jobType: PublishJobType.SKYSPARK_ENABLE_RULES,
            label: PublishStepperLabels.AUTO_CONFIGURE_RULES
        }),
        new PublishStepper({
            taskType: PublishTaskType.ONBOARDING_IMPORT_MODEL,
            jobType: PublishJobType.DASHBOARD_CONFIGURATION,
            label: PublishStepperLabels.ENABLE_DASHBOARDS
        }),
        new PublishStepper({
            taskType: PublishTaskType.ONBOARDING_IMPORT_MODEL,
            jobType: PublishJobType.SKYSPARK_IMPORT_MODEL,
            label: PublishStepperLabels.EXPORT_MODEL_TO_SKYSPARK
        })
    ];

    getContextDiscoveryStatus(status) {
        return status.Status;
    }

    getCompleteOnboardingStatus(status, isAutomaticAnalyticsSetupFeatureEnabled) {
        let publishSteppers;

        const publishToSentienceStepper = new PublishStepper({
            taskType: PublishTaskType.PUBLISH_TO_SENTIENCE,
            label: PublishStepperLabels.PUBLISH_MODEL_TO_SENTIENCE
        });
        publishToSentienceStepper.status = this.getContextDiscoveryStatus(status);
        publishToSentienceStepper['isPublished'] = status.IsPublished;

        if (isAutomaticAnalyticsSetupFeatureEnabled) {
            let onboardingSteppers;
            let onBoardingAnalyticsSetup = this.getOnboardingJob(status, PublishTaskType.ONBOARDING_IMPORT_MODEL);

            if (onBoardingAnalyticsSetup) {
                onboardingSteppers = this.onboardingImportModelSteppers;
            } else {
                onBoardingAnalyticsSetup = this.getOnboardingJob(status, PublishTaskType.ONBOARDING_ANALYTICS_SETUP);
                onboardingSteppers = this.onboardingAnalyticsSteppers;
            }

            _.forEach(onboardingSteppers, onboardingStepper => {
                onboardingStepper.status = this.getStatusOfOnboardingJob(onBoardingAnalyticsSetup, onboardingStepper.jobType);
                if (onBoardingAnalyticsSetup) {
                    onboardingStepper['jobStatus'] = onBoardingAnalyticsSetup.Status;
                }
            });
            publishSteppers = [publishToSentienceStepper].concat(onboardingSteppers);
        } else {
            publishSteppers = [publishToSentienceStepper];
        }

        return publishSteppers;
    }

    getOnboardingJob(status, taskType) {
        if (status.hasOwnProperty('OnBoardingTasks')) {
            const onBoardingAnalyticsSetup = status.OnBoardingTasks.filter(tasks => tasks.TaskType === taskType);
            return onBoardingAnalyticsSetup[0];
        } else {
            return null;
        }
    }

    getStatusOfOnboardingJob(onBoardingTasks, jobType: string) {
        if (onBoardingTasks && jobType) {
            return _.result(_.find(onBoardingTasks.JobStatus, { 'JobType': jobType }), 'Status');
        }
        return undefined;
    }

    isSetupAnalyticsCompletedSuccessfully(status): boolean {
        const completeOnboardingStatus = this.getCompleteOnboardingStatus(status, true);
        return _.every(completeOnboardingStatus, ({ status }) => status
            && (status.toUpperCase() === CdtStatus.COMPLETED || status.toUpperCase() === CdtStatus.SUCCEEDED));
    }

    getStatusOfPublishWithAutomaticAnalyticsSetup(completeOnboardingStatus, isAutomaticAnalyticsSetupFeatureEnabled): CdtStatus {
        if (completeOnboardingStatus) {
            if (_.every(completeOnboardingStatus, ({ status }) => !status)) {
                return undefined;
            }

            let anySuccess: boolean = false;

            for (const object of completeOnboardingStatus) {

                if (object['taskType'] === PublishTaskType.PUBLISH_TO_SENTIENCE) {
                    if (object['status']) {
                        if (object['status'].toUpperCase() === CdtStatus.PUBLISH_IN_PROGRESS) {
                            return CdtStatus.PUBLISH_IN_PROGRESS;
                        } else if (object['status'].toUpperCase() === CdtStatus.PUBLISH_FAILED) {
                            return CdtStatus.PUBLISH_FAILED;
                        }
                    }
                } else if (isAutomaticAnalyticsSetupFeatureEnabled) {
                    if (this.isStatusValueIncomplete(object['jobStatus'])) {
                        return CdtStatus.PUBLISH_IN_PROGRESS;
                    }
                    if (object['status']) {
                        if (this.isStatusValueIncomplete(object['status'])) {
                            return CdtStatus.PUBLISH_IN_PROGRESS;
                        }

                        if (object['status'].toUpperCase() === CdtStatus.FAILED) {
                            return CdtStatus.PUBLISH_FAILED;
                        } else {
                            anySuccess = true;
                        }
                    }
                }
            }

            if (anySuccess ||
                (completeOnboardingStatus[0]['status']
                    && completeOnboardingStatus[0]['status'].toUpperCase() === CdtStatus.COMPLETED
                    && !_.isNil(completeOnboardingStatus[0]['isPublished']))) {
                return CdtStatus.SUCCEEDED;
            }
        }

        return undefined;
    }

    private isStatusValueIncomplete(statusValue: string): boolean {
        if (statusValue && (statusValue.toUpperCase() !== CdtStatus.COMPLETED
            && statusValue.toUpperCase() !== CdtStatus.SUCCEEDED
            && statusValue.toUpperCase() !== CdtStatus.FAILED)) {
            return true;
        }
        return false;

    }

    getStatusOfPublishHistory(status): CdtStatus {
        const historyStatus = status.HistoryPublishStatus;
        if (historyStatus && historyStatus !== CdtStatus.COMPLETED) {
            return historyStatus;
        }

        const onBoardingImportHistoryJob = this.getOnboardingJob(status, PublishTaskType.ONBOARDING_IMPORT_HISTORY);
        if (!_.isNil(onBoardingImportHistoryJob)) {
            if (onBoardingImportHistoryJob.Status.toUpperCase() === CdtStatus.COMPLETED
                || onBoardingImportHistoryJob.Status.toUpperCase() === CdtStatus.SUCCEEDED) {
                return CdtStatus.COMPLETED;
            } else if (onBoardingImportHistoryJob.Status.toUpperCase() === CdtStatus.FAILED) {
                return CdtStatus.PUBLISH_FAILED;
            } else {
                return CdtStatus.PUBLISH_IN_PROGRESS;
            }
        }

        if (historyStatus) {
            return historyStatus;
        }

        return undefined;
    }
}
